export type CropArea = {
  height: number;
  width: number;
  x: number;
  y: number;
};

export interface CropImageOptions {
  imageSrc: string;
  cropArea: CropArea;
  originalFileName: string;
  /** Output MIME type. Defaults to image/png. Use image/jpeg for smaller payloads. */
  outputMime?: string;
  /** Suffix appended to the basename of the original file. Defaults to "-cropped". */
  outputSuffix?: string;
  /** Quality 0..1 for lossy formats (jpeg/webp). Ignored for png. */
  quality?: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    // Future-proof: callers always pass a blob URL today, but if a CDN URL
    // ever flows through here we don't want canvas tainting to break export.
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Failed to load image for cropping."));
    image.src = src;
  });
}

function mimeToExtension(mime: string): string {
  if (mime === "image/jpeg") return ".jpg";
  if (mime === "image/webp") return ".webp";
  return ".png";
}

export async function cropImage({
  imageSrc,
  cropArea,
  originalFileName,
  outputMime = "image/png",
  outputSuffix = "-cropped",
  quality,
}: CropImageOptions): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas is not available for image cropping.");
  }

  canvas.width = cropArea.width;
  canvas.height = cropArea.height;

  context.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    cropArea.width,
    cropArea.height,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (next) => {
        if (!next) {
          reject(new Error("Failed to create cropped image."));
          return;
        }
        resolve(next);
      },
      outputMime,
      quality,
    );
  });

  const baseName = originalFileName.replace(/\.[^.]+$/, "") || "image";
  const ext = mimeToExtension(outputMime);
  return new File([blob], `${baseName}${outputSuffix}${ext}`, {
    type: outputMime,
  });
}
